import type { RouteRecordRaw, RouteRecordSingleView } from 'vue-router'

import { registerIntegrationsMessages } from './i18n'
import { MCP_CONNECT_ROUTE, MCP_CONNECTIONS_ROUTE, MCP_SHELL_CONNECT_URL } from './mcpRouteTargets'
import { CONNECTIONS_ROUTE, SLACK_SETTINGS_ROUTE } from './routeTarget'

import { useLocaleStore } from '@/shared/i18n'

/**
 * 12's settings CHILD route — appended to the `/settings` parent by
 * `installIntegrationsSettingsNav`, never composed by 05 into the app layout.
 *
 * ⚠ This deliberately departs from contract 12 §11, which declares `integrationRoutes` as a
 * standalone app-layout route (`path: 'connections'`) composed by the shell, and never mentions
 * module 22 — that contract was written before 22's host seam existed (grep for
 * `registerSettingsChildRoutes` in `12-integrations.md` returns nothing). Contract 22 §8 names
 * `installIntegrationsSettingsNav(reg)` explicitly, and v3's live behaviour agrees: Connections is
 * `/workspace-settings/connections`, a pane INSIDE the settings shell with its own settings-nav
 * entry (`useWorkspaceSettings.js:214-219`). Hoisting it to the app layout would lose that parity
 * and would not prove the seam ORP-18893 precondition 5 exists to prove. Contract 12 §11 is amended
 * by ORP-19897.
 *
 * The path stays a bare `'connections'` so the resolved URL is `/settings/connections`.
 *
 * 22's kit needs 22's own messages (`workspaceSettings.pane.*`, `workspaceSettings.error.*`); those
 * are registered by the `/settings` PARENT's `beforeEnter`, which vue-router runs before this one.
 */
type SettingsRouteComponent = RouteRecordSingleView['component']

export function createIntegrationSettingsRoutes(
  connectionsComponent: SettingsRouteComponent = () => import('./components/ConnectionsPage.vue'),
): RouteRecordRaw[] {
  return [
    {
      path: 'connections',
      name: CONNECTIONS_ROUTE.name,
      component: connectionsComponent,
      meta: {
        titleKey: 'integrations.connections.title',
        subtitleKey: 'integrations.connections.subtitle',
        navKey: 'connections',
      },
      beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
    },
    {
      // The AI assistants (MCP) pane moved to mcp.orphex.co (ORP-27468); the in-app connections
      // screen is gone and an old link — a bookmark, the gateway's "View connections" — lands on the
      // app's home, as v3 does. The record keeps its name so the legacy ingress still resolves it.
      path: 'mcp/connections',
      name: MCP_CONNECTIONS_ROUTE.name,
      redirect: '/',
    },
    {
      // ORP-27946 — `/settings/slack`. Also the Slack install's return destination: the backend maps
      // `redirect_identifier: 'settings'` to this path and appends `status` / `reason`, so the path
      // is the backend's to choose, not ours. No settings-nav entry, matching v3: it is reached from
      // the Slack notification channel and from the install return.
      path: 'slack',
      name: SLACK_SETTINGS_ROUTE.name,
      component: () => import('./components/slack/SlackSettingsPage.vue'),
      meta: {
        titleKey: 'integrations.slackSettings.pageTitle',
        subtitleKey: 'integrations.slackSettings.pageSubtitle',
        navKey: 'notifications',
      },
      beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
    },
  ]
}

export const integrationSettingsRoutes: RouteRecordRaw[] = createIntegrationSettingsRoutes()

/** Consent now lives on the standalone MCP shell (ORP-21828), not here. */

/** Signed-in MCP onboarding return. It intentionally sits outside the settings frame. */
export const mcpRoutes: RouteRecordRaw[] = [
  {
    path: 'settings/mcp/connect',
    name: MCP_CONNECT_ROUTE.name,
    component: () => import('./components/mcp/McpConnectPage.vue'),
    /**
     * The cut-over transition redirect (ORP-21843). ORP-21842 repoints the backend's
     * `MCP_ONBOARDING_CONNECT_URL` and the Auth0 post-login Action at the shell, so this address
     * only ever sees Auth0 transactions that were already in flight and stale links — both of
     * which carry the `session_token` + `state` the shell needs, hence `fullPath` verbatim.
     *
     * A record `redirect` is the ONLY hook that runs before the guard chain (`pushWithRedirect`
     * calls it ahead of `navigate()`, which is where `beforeEach` lives), so the hard navigation
     * is already in flight before authGuard can bounce a signed-out visitor to Login or
     * permissionGuard can pull a zero-workspace account onto onboarding. `mcpAllowed` below keeps
     * the ORP-21656 branch out of it either way; the ordering is what covers the other two.
     *
     * Returning home with the query STRIPPED: the returned location inherits `to.query`/`to.hash`
     * otherwise, which would park the handoff token in a history entry during the split second
     * before the browser leaves. Rollback is deleting this option and the empty `children` that
     * types it — the page, its route and every other line here stay put.
     */
    redirect: ({ fullPath, path }) => {
      window.location.replace(MCP_SHELL_CONNECT_URL + fullPath.slice(path.length))
      return { path: '/', query: {}, hash: '' }
    },
    // Registers no routes. `RouteRecordSingleView` declares `redirect?: never`, so carrying BOTH a
    // component and a redirect — which is what keeps the page recoverable — needs this shape.
    children: [],
    // `mcpAllowed` (ORP-21656): this IS the MCP handoff return — bouncing it would break the
    // very flow that brings an MCP-only user in.
    meta: { mcpAllowed: true, titleKey: 'integrations.mcp.connect.title' },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
]

/**
 * The Slack per-user link page — a STANDALONE route, not a `/settings` child (ORP-19904).
 *
 * The path reads like a settings pane but the page is not one: it is a one-shot task the user
 * reaches from a Slack DM, so the settings nav chrome would be noise. The legacy frontend made the
 * same call — its `/settings/slack-link` sits in the standalone route file, outside the settings
 * host — and this is why the record carries the full absolute path instead of joining
 * `integrationSettingsRoutes` above.
 *
 * ⚠ The path is NOT ours to choose. The backend mints the invite URL from one constant
 * (`SLACK_LINK_PATH`), so both frontends must serve exactly this path; that constant pointed at an
 * invented path for a release and opened a blank page for every user (ORP-19716). Moving it means
 * moving the backend and the other frontend in the same change.
 *
 * `requiresAuth` is explicit: the whole point is to bind the Slack identity to the *signed-in*
 * Orphex account, so an anonymous visit must round-trip through login rather than fail silently.
 */
export const slackLinkRoutes: RouteRecordRaw[] = [
  {
    path: '/settings/slack-link',
    name: 'SlackLink',
    component: () => import('./components/SlackLinkPage.vue'),
    meta: {
      layout: 'blank',
      requiresAuth: true,
      // `mcpAllowed` (ORP-21656) — the backend mints this URL for every account type.
      mcpAllowed: true,
      titleKey: 'integrations.slackLink.title',
    },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
]

/**
 * The Google Sheets add-on approval page (ORP-27820) — standalone, like the Slack link page.
 *
 * ⚠ The path is fixed by the add-on's production build: it opens exactly this address with
 * `#pairing=<uuid>`. `requiresAuth` sends a signed-out visit through login with `next` set to the
 * full path, hash included, so the same pairing resumes after sign-in.
 */
export const sheetsAddonRoutes: RouteRecordRaw[] = [
  {
    path: '/sheets-addon/connect',
    name: 'SheetsAddonConnect',
    component: () => import('./components/SheetsAddonConnectPage.vue'),
    meta: {
      layout: 'blank',
      requiresAuth: true,
      titleKey: 'integrations.sheetsAddon.title',
    },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
]

/**
 * The Slack workspace-install return — the first child of 05's `/callback` parent (ORP-19904).
 *
 * The parent shipped inert precisely so its children could arrive on their owning feature's ticket
 * (`app/router/routes/callbacks.ts`); this is 12's, per contract 12 §2 ("the new home for every
 * OAuth / install / provider-return callback"). `layout: 'blank'` is inherited from the parent, so
 * it is deliberately not repeated here.
 *
 * `requiresAuth: true` because the install is started from inside the app by a signed-in operator;
 * unlike the Shopify return, nothing hands us a token on the way back in.
 */
export const slackCallbackRoutes: RouteRecordRaw[] = [
  {
    path: 'slack',
    name: 'SlackInstallCallback',
    component: () => import('./components/SlackInstallCallback.vue'),
    meta: { requiresAuth: true, titleKey: 'integrations.slackInstall.title' },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
]

/** Public provider returns. They inherit BlankLayout from the shell callback parent. */
export const integrationCallbackRoutes: RouteRecordRaw[] = [
  {
    path: 'shopify',
    name: 'ShopifyCallback',
    component: () => import('./components/ShopifyCallbackView.vue'),
    meta: { requiresAuth: false, titleKey: 'integrations.callback.working' },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
  {
    path: 'oauth',
    name: 'OAuthCallback',
    component: () => import('./components/OAuthCallbackView.vue'),
    meta: { requiresAuth: false, titleKey: 'integrations.callback.working' },
    beforeEnter: () => registerIntegrationsMessages(useLocaleStore().locale),
  },
]
